import { broker } from '#~/agent/broker.js';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import type { State, StatusDevice } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { StudioDevice } from '@tryiris-ai/common.protojs/studio/v1/studio';

import type { Proxy } from '#~/agent/types.js';
import type { Discovery } from '#~/device/discovery/types.js';
import { pbToJsDiscovery } from '#~/protobuf/device.js';
import { transformStatusBrowserDiscoveryToDiscovery } from '#~/room/provider/utils.js';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { MultiDeviceSelectorDeviceInstace, StudioSelectorInstance } from './types.js';

export const multiProxyStateQuery = <U>(
  selector: (state: T_Maybe<State>, id: Proxy['id']) => Record<Agent['id'], U>
) => {
  const result: Record<Agent['id'], U> = {};

  for (const proxy of broker) {
    const agentId = proxy.id as Agent['id'];

    if (proxy && proxy.state) {
      result[agentId] = selector(proxy.state as T_Maybe<State>, proxy.id) as U;
    }
  }

  return result;
};

export const multiDeviceStateQuery = <U>(
  // The selector returns the PER-DEVICE value; this query owns keying the
  // result map by device id (see MultiDeviceSelector in types.ts).
  selector: (device: MultiDeviceSelectorDeviceInstace) => U
) => {
  const result: Record<Device['id'], U> = {};

  for (const proxy of broker) {
    if (proxy && proxy.state) {
      for (const deviceIdRaw in proxy.state.control?.device) {
        const deviceId = deviceIdRaw as Device['id'];
        const control = proxy.state.control?.device?.[deviceId];
        const status = proxy.state.status?.device?.[deviceId];
        const studio = proxy.state.control?.studio?.device?.[deviceId];

        result[deviceId] = selector({
          id: deviceId,
          control,
          status: status as T_Maybe<StatusDevice>,
          // Readonly state view; selectors only read it.
          studio: studio as T_Maybe<StudioDevice>
        });
      }
    }
  }

  return result;
};

export const studioStateQuery = <U>(selector: (studio: StudioSelectorInstance) => U) => {
  // Aggregate studio data from all bridges
  const aggregatedStudio: StudioSelectorInstance = {
    device: {},
    autoTrackingReservation: {},
    autoTrackingWorker: {},
    recordingDirectory: undefined,
    rtmpEgress: {
      $type: 'studio.v1.RTMPEgress' as const,
      destination: {}
    }
  };

  let hasAnyStudio = false;

  for (const proxy of broker) {
    if (proxy && proxy.state) {
      const studio = proxy.state.control?.studio;

      if (!studio) {
        continue; // Skip this proxy and check the next one
      }

      hasAnyStudio = true;

      // Merge devices from all bridges
      if (studio.device) {
        aggregatedStudio.device = { ...aggregatedStudio.device, ...studio.device };
      }

      // Merge auto tracking reservations from all bridges
      if (studio.autoTrackingReservation) {
        aggregatedStudio.autoTrackingReservation = {
          ...aggregatedStudio.autoTrackingReservation,
          ...studio.autoTrackingReservation
        };
      }

      // Merge auto tracking workers from all bridges
      if (studio.autoTrackingWorker) {
        aggregatedStudio.autoTrackingWorker = {
          ...aggregatedStudio.autoTrackingWorker,
          ...studio.autoTrackingWorker
        };
      }

      // Use recording directory from first bridge that has it
      if (studio.recordingDirectory && !aggregatedStudio.recordingDirectory) {
        aggregatedStudio.recordingDirectory = studio.recordingDirectory;
      }

      // Merge RTMP destinations from all bridges
      if (studio.rtmpEgress?.destination) {
        const currentDestinations = aggregatedStudio.rtmpEgress?.destination || {};
        aggregatedStudio.rtmpEgress = {
          $type: 'studio.v1.RTMPEgress' as const,
          ...aggregatedStudio.rtmpEgress,
          destination: { ...currentDestinations, ...studio.rtmpEgress.destination }
        };
      }
    }
  }

  // Return undefined if no studio data found across all bridges
  if (!hasAnyStudio) {
    return undefined;
  }

  return selector(aggregatedStudio);
};

export interface DiscoveryDevice {
  id: Device['id'];
  value: Discovery;
  discoveredProxyIDs: Agent['id'][];
}

export const multiProxyDeviceDiscoveryStateQuery = (
  state: T_Maybe<State>,
  proxyID: Proxy['id']
): Record<Agent['id'], DiscoveryDevice> => {
  let devices: Record<string, DiscoveryDevice> = {};

  if (!state) {
    return devices;
  }

  const encoderDiscoveries = state?.status?.discovery ?? {};

  const browserDiscoveries = Object.values(state?.control?.reservation ?? {})
    .filter((res) => {
      return res?.advertisedDiscoveries;
    })
    .filter((res) => {
      return res?.advertisedDiscoveries !== undefined;
    });

  for (const discoveryID of Object.keys(encoderDiscoveries)) {
    const discovery = encoderDiscoveries[discoveryID];

    if (!discovery) {
      continue;
    }

    const jsDiscovery = pbToJsDiscovery(proxyID, discoveryID as Discovery['id'], discovery);

    if (!jsDiscovery) {
      continue;
    }

    // Additional validation for critical discovery properties
    if (!jsDiscovery.name || (!jsDiscovery.video && !jsDiscovery.control && !jsDiscovery.audio)) {
      continue;
    }

    const type = (jsDiscovery?.video ?? jsDiscovery?.control)?.protocol ?? 'unknown';

    // Enhanced ID generation for better embedded camera deduplication
    const host = (<any>jsDiscovery.control)?.host ?? (<any>jsDiscovery.video)?.host;
    const name = (<any>jsDiscovery.video)?.name ?? jsDiscovery.name;
    const audioHost = (<any>jsDiscovery.audio)?.host;
    const audioPath = (<any>jsDiscovery.audio)?.path;

    // For embedded cameras (IRIS protocol), create a more robust identifier
    const isIrisDevice = jsDiscovery.irisEnabled || type === 'iris';
    const txtId = (jsDiscovery.video as { id?: string } | undefined)?.id;
    let identifier;

    if (isIrisDevice) {
      // Identity is the camera (TXT id, else host+name), never the discovering
      // proxy — two bridges on one LAN must merge into one row.
      identifier = txtId
        ? `iris:${txtId}`
        : host && name
          ? `iris:${host}:${name}`
          : `${type}:${host ?? name ?? 'unknown'}`;
    } else if (type === 'ndi' && host && name) {
      // One host can publish several NDI senders (encoder channels); each sender
      // is its own source, so the sender name joins the host in the identity.
      identifier = `ndi${host}|${name}`;
    } else {
      // Use original logic for other device types
      identifier = type + (host ?? name ?? audioHost ?? audioPath ?? 'unknown');
    }

    const safeID = identifier as Device['id'];

    devices[safeID] = {
      id: safeID,
      value: jsDiscovery,
      discoveredProxyIDs: [proxyID as Agent['id']]
    };
  }

  for (const discoveries of browserDiscoveries) {
    if (discoveries?.advertisedDiscoveries) {
      for (const discoveryID of Object.keys(discoveries.advertisedDiscoveries)) {
        const discovery = discoveries.advertisedDiscoveries[discoveryID];
        const safeID = idForDeviceID(proxyID + discoveryID) as Device['id'];

        if (!discovery) {
          continue;
        }

        const jsDiscovery = transformStatusBrowserDiscoveryToDiscovery(proxyID, discovery);

        if (!jsDiscovery) {
          continue;
        }

        devices[safeID!] = {
          id: safeID as Device['id'],
          value: jsDiscovery,
          discoveredProxyIDs: [proxyID as Agent['id']]
        };
      }
    }
  }

  // Resolve authoritative identity before merging proxies: identical addresses
  // on different LANs must not transfer models. Transport and ownership stay put.
  const irisModels = new Map<string, Discovery['modelId']>();
  for (const { value } of Object.values(devices)) {
    if (value.irisEnabled && value.modelId && value.video && 'host' in value.video) {
      irisModels.set(value.video.host, value.modelId);
    }
  }
  for (const device of Object.values(devices)) {
    const value = device.value;
    const config = value.control ?? value.video;
    const modelId = config && 'host' in config ? irisModels.get(config.host) : undefined;
    if (!value.irisEnabled && modelId) {
      const { brandId: _fingerprintedBrand, ...native } = value;
      device.value = { ...native, modelId };
    }
  }

  return devices;
};

const safeDeviceIDMap: Record<string, string> = {};

let currentIDI = 0;

const idForDeviceID = (deviceID: string) => {
  if (safeDeviceIDMap[deviceID]) {
    return safeDeviceIDMap[deviceID];
  }

  const newID = `device${currentIDI++}`;

  safeDeviceIDMap[deviceID] = newID;

  return newID;
};

export const transformMultiProxyDeviceDiscoveryToDiscovery = (
  proxyMaps: Record<Proxy['id'], Record<Agent['id'], DiscoveryDevice>>
): Record<Device['id'], DiscoveryDevice> => {
  const result: Record<Device['id'], DiscoveryDevice> = {};
  const deviceProcessingLog: Record<string, any> = {};

  for (const [, proxy] of Object.entries(proxyMaps)) {
    for (const deviceId in proxy) {
      const device = proxy[deviceId as Agent['id']];

      // Add validation to prevent null/undefined devices
      if (!device || !device.id || !device.value) {
        continue;
      }

      // Track device processing for debugging
      if (!deviceProcessingLog[device.id]) {
        deviceProcessingLog[device.id] = {
          deviceId: device.id,
          name: device.value.name,
          proxyIds: [],
          mergeAttempts: 0,
          finalProxyIds: []
        };
      }

      const log = deviceProcessingLog[device.id];
      log.proxyIds.push(device.discoveredProxyIDs[0]);
      log.mergeAttempts++;

      const existingDevice = result[device.id];

      if (existingDevice) {
        const proxyId = device.discoveredProxyIDs[0];

        if (!proxyId) {
          continue;
        }

        if (existingDevice.discoveredProxyIDs.includes(proxyId)) {
          continue;
        }

        // Enhanced validation for embedded cameras
        const isEmbeddedCamera =
          device.value.irisEnabled || device.value.video?.protocol === 'iris';

        if (isEmbeddedCamera) {
          const existingHost = (existingDevice.value.video as any)?.host;
          const newHost = (device.value.video as any)?.host;
          const existingName = existingDevice.value.name;
          const newName = device.value.name;

          // Check if this is the same physical device

          // Only merge if host and name match (same physical device)
          if (existingHost === newHost && existingName === newName) {
            existingDevice.discoveredProxyIDs.push(proxyId);
            log.finalProxyIds = [...existingDevice.discoveredProxyIDs];
          } else {
            // Different embedded camera - create separate entry with modified ID to avoid conflicts
            const separateDeviceId = `${device.id}_${proxyId}` as Device['id'];

            result[separateDeviceId] = {
              ...device,
              id: separateDeviceId,
              value: {
                ...device.value,
                agentIds: device.discoveredProxyIDs
              }
            };
          }
        } else {
          existingDevice.discoveredProxyIDs.push(proxyId);
          log.finalProxyIds = [...existingDevice.discoveredProxyIDs];
        }
      } else {
        result[device.id] = { ...device };
        log.finalProxyIds = [...device.discoveredProxyIDs];
      }
    }
  }

  const safeIDResult: Record<Device['id'], DiscoveryDevice> = {};

  for (const deviceId in result) {
    const device = result[deviceId as Device['id']];
    const safeID = idForDeviceID(deviceId) as Device['id'];

    if (!device) {
      continue;
    }

    safeIDResult[safeID] = {
      ...device,
      value: {
        ...device.value,
        agentIds: device.discoveredProxyIDs
      }
    };
  }

  return safeIDResult;
};
